import axios from 'axios';
import { config } from '../config';

export type NotificationStatus = 'unread' | 'read';

export type Notification = {
  id: string;
  notificationId: string;
  recipientId: string;
  title: string;
  message: string;
  metadata: Record<string, unknown> | null;
  status: NotificationStatus;
  scheduledFor: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PaginatedNotifications = {
  data: Notification[];
  pagination: {
    limit: number;
    offset: number;
    count: number;
    hasMore: boolean;
  };
};

export type CreateNotificationInput = {
  recipientIds: string[];
  title: string;
  message: string;
  metadata?: Record<string, unknown>;
  scheduledFor?: string;
};

export type ListParams = {
  recipientId: string;
  status?: NotificationStatus;
  limit?: number;
  offset?: number;
};

const api = axios.create({ baseURL: config.apiUrl });

function auth(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export type Recipient = {
  recipientId: string;
  name: string;
  email: string;
};

export const NotificationsApi = {
  health(): Promise<{ status: string; db: string }> {
    return api.get('/health').then((r) => r.data);
  },

  login(username: string, password: string): Promise<string> {
    return api
      .post<{ token: string }>('/api/v1/auth/login', { username, password })
      .then((r) => r.data.token);
  },

  searchRecipients(q: string, token: string): Promise<Recipient[]> {
    return api
      .get<{ data: Recipient[] }>('/api/v1/recipients/search', { params: { q }, ...auth(token) })
      .then((r) => r.data.data);
  },

  create(input: CreateNotificationInput, token: string): Promise<Notification[]> {
    return api
      .post<{ data: Notification[] }>('/api/v1/notifications', input, auth(token))
      .then((r) => r.data.data);
  },

  list(params: ListParams, token: string): Promise<PaginatedNotifications> {
    return api
      .get<PaginatedNotifications>('/api/v1/notifications', { params, ...auth(token) })
      .then((r) => r.data);
  },

  markAsRead(id: string, token: string): Promise<Notification> {
    return api
      .patch<{ data: Notification }>(`/api/v1/notifications/${id}/read`, {}, auth(token))
      .then((r) => r.data.data);
  },
};
